import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import T from '@wojtekmaj/react-t';

import {
  ButtonAndRoadblockWrapper,
  ButtonWrapper,
  ContentWrapper,
  Description,
  Hint,
  Image,
  ImagePlaceholder,
  ImageWrapper,
  Name,
  Price,
} from './index.styles';

import EmailVerificationRoadblock from '../../../../../../common/email_verification_roadblock';
import PageWrapper from '../../../../../../page_wrapper';
import HeaderWithButton from '../../../../header_with_button';
import ActivateButton from './activate_button';

import useI18nText from '../../../../../../../hooks/useI18nText';

import { formatNumber } from '../../../../../../../utils/helpers';

import {
  currentUserState,
  transactionsQuery,
  vendorCardRewardQuery,
  vendorCardSubscriptionState,
} from '../../../../../../../store';

import lockIcon from '../../../../../../../static/lock.svg';
import rewardIcon from '../../../../../../../static/reward.svg';

import type { Reward, Transaction } from '@rewardopl/types';

export default function RewardPage() {
  const navigate = useNavigate();
  const { reward_id: rewardId } = useParams<{ reward_id?: Reward['_id'] }>();

  if (!rewardId) {
    throw new Error('rewardId is undefined');
  }

  const cardSubscription = useAtomValue(vendorCardSubscriptionState);
  const currentUser = useAtomValue(currentUserState);

  const { _id: cardSubscriptionId, balance } = cardSubscription;

  const { is_email_verified: isEmailVerified = true } = currentUser;

  const setTransactions = useSetAtom(transactionsQuery(cardSubscriptionId));
  const reward = useAtomValue(vendorCardRewardQuery(rewardId));

  const {
    description,
    description_i18n: descriptionI18n,
    name,
    name_i18n: nameI18n,
    picture,
    price = 0,
  } = reward ?? {};

  const nameString = useI18nText(nameI18n, name);
  const descriptionString = useI18nText(descriptionI18n, description ?? '');

  if (!reward) {
    return <Navigate to=".." />;
  }

  async function onSuccess(nextTransaction: Transaction) {
    setTimeout(() => {
      navigate(`../../reward_received/${nextTransaction._id}`);
    }, 0);
  }

  const isAvailable = balance >= price;

  return (
    <>
      <HeaderWithButton>Reward</HeaderWithButton>
      <PageWrapper>
        <ContentWrapper>
          <ImageWrapper>
            {picture ? (
              <Image alt={nameString} src={`/api/uploads/${picture}?w=${320 * devicePixelRatio}`} />
            ) : (
              <ImagePlaceholder alt={nameString} src={rewardIcon} />
            )}
          </ImageWrapper>
          <Name>{nameString}</Name>
          <Price>
            {isAvailable ? null : <img alt="" src={lockIcon} />}
            {formatNumber(price)} <T>{`point${price === 1 ? '' : 's'}`}</T>
          </Price>
          {descriptionString ? <Description>{descriptionString}</Description> : null}
          <Hint>
            {isAvailable ? (
              <T price={price}>
                {
                  'After clicking “Get” you’ll receive reward, and {price} points will be taken from your account.'
                }
              </T>
            ) : (
              <T>You don’t have enough points to get this reward.</T>
            )}
          </Hint>
          <ButtonAndRoadblockWrapper>
            <ButtonWrapper>
              <ActivateButton
                disabled={!isAvailable || !isEmailVerified}
                onSuccess={onSuccess}
                reward={reward}
                setTransactions={setTransactions}
              />
            </ButtonWrapper>
            {isEmailVerified ? null : <EmailVerificationRoadblock />}
          </ButtonAndRoadblockWrapper>
        </ContentWrapper>
      </PageWrapper>
    </>
  );
}
